import React, { useEffect, useState } from 'react';
import WeeklyHour from '../weekly-cal-view/WeeklyHour';
import BlockedTimeBox from './BlockedTimeBox';
import WeeklyHourLabel from '../weekly-cal-view/WeeklyHourLabel';
import { Box, useTheme, Typography, Popper } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import ExoDialog from '../ExoDialog';
import ExoDrawer from '../ExoDrawer';

const ParticipantsBlockTimesPopper = ({
  open,
  anchorEl,
  exoWorkdayRef,
  blockedUserTimesData,
  dataCollection,
  handleClose,
  onChange,
  onClose,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  // mobile view
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (dataCollection.startDate && dataCollection.startTime) {
      const newStart = moment(dataCollection.startTime)
        .set({
          year: moment(dataCollection.startDate).year(),
          month: moment(dataCollection.startDate).month(),
          date: moment(dataCollection.startDate).date(),
        })
        .toISOString();

      const newEnd = moment(dataCollection.endTime)
        .set({
          year: moment(dataCollection.startDate).year(),
          month: moment(dataCollection.startDate).month(),
          date: moment(dataCollection.startDate).date(),
        })
        .toISOString();

      onChange({
        ...dataCollection,
        startTime: newStart,
        endTime: newEnd,
      });
    }
  }, [dataCollection.startDate]);

  function doesTimeOverlapWithDay(startTime, endTime, dayDate) {
    const start = moment(startTime);
    const end = moment(endTime);
    const dayStart = moment(dayDate).startOf('day');
    const dayEnd = moment(dayDate).endOf('day');

    return start.isBefore(dayEnd) && end.isAfter(dayStart);
  }

  const Container = isSmallScreen ? ExoDrawer : Box;

  const zIndex = isSmallScreen ? 1400 : 1000;

  return (
    open && (
      <Container
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        placement="right-start"
        modifiers={[
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: anchorEl,
            },
          },
        ]}
        zIndex={zIndex}
        sx={{
          width: 'auto',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: isSmallScreen ? '50px' : 0,
        }}
        limitWidth
      >
        <Box
          className="flex flex-col gap-1 py-3 pr-3 rounded-md h-full"
          sx={{
            background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
            border: `1px solid ${colors.selected}`,
            width: '550px',
            maxHeight: '90vh',
            backdropFilter: 'blur(5px)',
            overflowY: 'auto',
            marginLeft: '10px', // spacing between the two boxes
          }}
        >
          <Box
            className="flex flex-row w-full h-full overflow-y-auto"
            ref={exoWorkdayRef}
          >
            <Box
              className="flex flex-col h-full"
              sx={{
                paddingTop: `70px`, // Equivalent to 55 + (30 / 2)
                paddingLeft: '5px',
                gap: '30px', // 30px * (4 - 1)
              }}
            >
              {[...Array(14)].map((e, i) => (
                <WeeklyHourLabel
                  key={i}
                  width={40}
                  resHeight={30}
                  hour={i + 6}
                />
              ))}
            </Box>

            {(blockedUserTimesData?.length > 0
              ? blockedUserTimesData
              : [
                  {
                    userId: 'no-user',
                    firstName: t('No Participants'),
                    lastName: '',
                    blockedTimes: [],
                  },
                ]
            ).map((user, index) => (
              <Box className="w-full" key={user.userId || index}>
                {/* Sticky Header */}
                <Box
                  className="w-full sticky top-0 left-0 z-10 px-1 flex flex-col "
                  sx={{ height: `55px` }}
                >
                  <Box
                    className="rounded-lg w-full h-full transition-colors backdrop-blur-lg px-1 flex flex-col justify-center items-center overflow-hidden"
                    sx={{
                      bgcolor: colors.info + '30',
                      border: `1px solid ${colors.info + '30'}`,
                    }}
                  >
                    <Typography
                      variant="h5"
                      textAlign="center"
                      className="whitespace-nowrap h-full w-full justify-center items-center rounded-lg pt-1 flex"
                      fontWeight={600}
                    >
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography
                      variant="h6"
                      textAlign="center"
                      className="whitespace-nowrap h-full w-full flex justify-center items-center rounded-lg"
                    >
                      {moment(dataCollection.startDate).format('DD MMMM')}
                    </Typography>
                  </Box>
                </Box>

                {/* BODY with time slots */}
                <Box className="block relative w-full">
                  {[...Array(14)].map((_, i) => (
                    <Box className="flex flex-row w-full" key={i}>
                      <WeeklyHour
                        hour={i + 6}
                        hourEnd={20}
                        res={2}
                        resHeight={30}
                        noHours={index !== 0}
                      />
                    </Box>
                  ))}

                  {user.blockedTimes
                    ?.filter((bt) => moment(bt.startTime))
                    .map((bt, idx) => (
                      <BlockedTimeBox
                        key={idx}
                        blockedTime={bt}
                        dataCollection={dataCollection}
                      />
                    ))}

                  {/* Always show selection range */}
                  {doesTimeOverlapWithDay(
                    dataCollection.startTime,
                    dataCollection.endTime,
                    dataCollection.startDate
                  ) && (
                    <BlockedTimeBox
                      key={`selection-${user.userId || index}`}
                      blockedTime={{
                        startTime: dataCollection.startTime,
                        endTime: dataCollection.endTime,
                        wholeDay: false,
                      }}
                      dataCollection={dataCollection}
                      isSelection
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    )
  );
};

export default ParticipantsBlockTimesPopper;
